/**
 * Central react-query retry policy (ISP-9999, nemesis-0824-systemic-4xx-retry-storm).
 *
 * WHY: `new QueryClient({})` inherited react-query's default `retry: 3`, which
 * retries EVERY failure — including definitive client errors (400/404) that can
 * NEVER succeed on a retry. Measured live: one bad detail nav (/wheel/bad-id,
 * /messages/bad-id, /gallery/bad-id, /builder/bad-id) fired 2–12 pointless
 * requests with exponential backoff, and delayed the user-facing error page
 * ~13–18s while the loader sat through the backoff.
 *
 * FIX: a 4xx short-circuits to zero retries and surfaces the error immediately.
 * Transient failures (5xx, network — no `error.response`) still recover, up to
 * MAX_TRANSIENT_RETRIES.
 *
 * CARVE-OUT (fix-1004-fe-dev-red-clusters-second-r2): 429 (Too Many Requests)
 * is NOT a definitive client error like 400/404 — the request will succeed
 * once the throttle window passes (same BE contract already covered by
 * `home/api/v1/tests/test_login_throttle.py`; FE half pinned by
 * `src/utils/pollDefaultBackoff.e18.test.jsx`). Lumping it in with 400/404
 * silently broke that poll-surface backoff/converge contract: a rate-limited
 * poll (`useNotifications`, `useExploreFeed`, etc., all riding this app-wide
 * default) surfaced an error on the FIRST 429 instead of backing off, with no
 * code path ever retrying it again. 429 is treated as transient, the same
 * bucket as 5xx/network.
 *
 * Error shape: axios (this app uses axios ^1.6.0). An axios error carries the
 * HTTP status at `error.response.status`; a fallback to `error.status` covers a
 * non-axios error object defensively. A network error has no `.response`, so its
 * status is undefined → treated as transient (retryable).
 *
 * This is the SAME logic already inlined in services/capture.js (`captureRetry`);
 * this module is the app-wide default applied at the QueryClient level so every
 * detail route inherits it without a per-query opt-in.
 */

export const MAX_TRANSIENT_RETRIES = 2;

/** Read the HTTP status off an axios (or axios-shaped) error, or undefined. */
export const errorStatus = (error) => error?.response?.status ?? error?.status;

/** True when the status is a definitive client error (4xx, excluding the
 * transient 429 rate-limit carve-out above) — never retry these. */
export const isDefinitiveClientError = (error) => {
  const s = errorStatus(error);
  return typeof s === "number" && s >= 400 && s < 500 && s !== 429;
};

/**
 * react-query `retry` predicate. `failureCount` starts at 1 on the first failure.
 * - 4xx (excluding 429) → false (0 retries, error surfaces immediately)
 * - 429 / 5xx / network / no status → retry up to MAX_TRANSIENT_RETRIES
 */
export const shouldRetryQuery = (failureCount, error) => {
  if (isDefinitiveClientError(error)) return false;
  return failureCount < MAX_TRANSIENT_RETRIES;
};
